/**
 * PortalLandingPages — Full-featured landing page builder
 *
 * Features: create, edit (all fields), duplicate, preview, publish,
 * hero image URL, CTA text/color, SEO meta, custom CSS,
 * countdown timer, testimonials, A/B variants.
 */

import React, { useState, useEffect, useCallback } from 'react';
import PortalLayout from '@/components/portal/PortalLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  FileText, Plus, Loader2, Eye, Copy, Check, Trash2, Pencil, Save, X,
  Clock, MessageSquare, Image, Palette, Code, CopyPlus, ArrowLeft, Search,
  LayoutGrid, FormInput, MousePointerClick, Languages, GripVertical,
  ArrowUp, ArrowDown, Type, ImageIcon, ListChecks,
} from 'lucide-react';
import { supabaseGet, supabasePost, supabaseDelete, supabasePatch } from '@/utils/supabaseRest';
import { useAuth } from '@/contexts/AuthContext';

interface Testimonial {
  name: string;
  quote: string;
  location?: string;
  rating?: number;
}

interface PageBlock {
  id: string;
  type: 'hero' | 'text' | 'image' | 'widget' | 'cta' | 'testimonial' | 'divider' | 'features';
  content: Record<string, unknown>;
}

interface FormField {
  id: string;
  label: string;
  type: 'text' | 'email' | 'phone' | 'select' | 'checkbox' | 'textarea';
  placeholder?: string;
  required: boolean;
  options?: string[]; // for select type
}

interface LandingPage {
  id: string;
  slug: string;
  title: string;
  headline: string | null;
  subheadline: string | null;
  template: string;
  vertical: string;
  hero_image_url: string | null;
  background_color: string;
  text_color: string;
  cta_text: string;
  cta_color: string;
  meta_title: string | null;
  meta_description: string | null;
  custom_css: string | null;
  countdown_enabled: boolean;
  countdown_end_time: string | null;
  countdown_text: string | null;
  testimonials: Testimonial[];
  blocks: PageBlock[];
  form_fields: FormField[];
  exit_intent_enabled: boolean;
  exit_intent_headline: string | null;
  exit_intent_cta: string | null;
  exit_intent_delay_seconds: number;
  language: string;
  translations: Record<string, Record<string, string>>;
  ab_variant: string;
  ab_parent_id: string | null;
  ab_traffic_percent: number;
  is_published: boolean;
  views: number;
  conversions: number;
  created_at: string;
}

const TEMPLATES = [
  { value: 'hero-widget', label: 'Hero + Widget' },
  { value: 'comparison', label: 'Comparison' },
  { value: 'testimonial', label: 'Testimonial' },
  { value: 'minimal', label: 'Minimal' },
  { value: 'long-form', label: 'Long Form' },
];

const VERTICALS = [
  'auto_insurance', 'home_insurance', 'health_insurance', 'life_insurance',
  'debt_relief', 'debt_consolidation', 'personal_loan', 'mortgage_refi',
  'solar', 'medicare', 'tax_relief', 'home_security', 'home_warranty',
];

type EditTab = 'content' | 'blocks' | 'design' | 'form' | 'seo' | 'countdown' | 'testimonials' | 'exit_intent' | 'languages' | 'css' | 'ab';

export default function PortalLandingPages() {
  const { user } = useAuth();
  const [pages, setPages] = useState<LandingPage[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<Partial<LandingPage>>({});
  const [editTab, setEditTab] = useState<EditTab>('content');
  const [saving, setSaving] = useState(false);

  const [formTitle, setFormTitle] = useState('');
  const [formHeadline, setFormHeadline] = useState('');
  const [formTemplate, setFormTemplate] = useState('hero-widget');
  const [formVertical, setFormVertical] = useState('auto_insurance');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await supabaseGet('landing_pages?order=created_at.desc');
      setPages((data || []).map((p: any) => ({ ...p, testimonials: p.testimonials || [] })));
    } catch (err) {
      console.error('Failed to load:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const generateSlug = (title: string) =>
    title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 40) || 'page-' + Date.now().toString(36);

  const create = async () => {
    if (!formTitle) { setError('Page title is required'); return; }
    if (!user?.id) { setError('You must be logged in'); return; }
    setCreating(true);
    setError(null);
    try {
      await supabasePost('landing_pages', {
        user_id: user.id, slug: generateSlug(formTitle), title: formTitle,
        headline: formHeadline || formTitle, template: formTemplate, vertical: formVertical,
        is_published: false, cta_text: 'Get Your Free Quote', cta_color: '#10b981',
      });
      setShowCreate(false); setFormTitle(''); setFormHeadline(''); load();
    } catch (err: any) {
      setError(err?.message || 'Failed to create page');
    } finally {
      setCreating(false);
    }
  };

  const startEdit = (page: LandingPage) => {
    setEditingId(page.id);
    setEditForm({ ...page });
    setEditTab('content');
  };

  const saveEdit = async () => {
    if (!editingId) return;
    setSaving(true);
    try {
      const { id, slug, views, conversions, created_at, ...updates } = editForm as any;
      await supabasePatch(`landing_pages?id=eq.${editingId}`, updates);
      setPages(prev => prev.map(p => p.id === editingId ? { ...p, ...updates } : p));
      setEditingId(null);
    } catch (err: any) {
      console.error('Save failed:', err);
    } finally {
      setSaving(false);
    }
  };

  const duplicatePage = async (page: LandingPage) => {
    if (!user?.id) return;
    try {
      const { id, slug, views, conversions, created_at, ...rest } = page;
      await supabasePost('landing_pages', {
        ...rest, user_id: user.id, slug: slug + '-copy-' + Date.now().toString(36),
        title: rest.title + ' (Copy)', is_published: false, views: 0, conversions: 0,
      });
      load();
    } catch (err) {
      console.error('Duplicate failed:', err);
    }
  };

  const deletePage = async (id: string) => {
    await supabaseDelete(`landing_pages?id=eq.${id}`).catch(() => {});
    setPages(prev => prev.filter(p => p.id !== id));
  };

  const copyUrl = (slug: string) => {
    navigator.clipboard.writeText(`https://intentdrive.ai/c/${slug}`);
    setCopied(slug); setTimeout(() => setCopied(null), 2000);
  };

  const updateEdit = (key: string, value: any) => setEditForm(f => ({ ...f, [key]: value }));

  // Testimonial helpers
  const addTestimonial = () => {
    const t = editForm.testimonials || [];
    updateEdit('testimonials', [...t, { name: '', quote: '', location: '', rating: 5 }]);
  };
  const updateTestimonial = (i: number, key: string, val: any) => {
    const t = [...(editForm.testimonials || [])];
    t[i] = { ...t[i], [key]: val };
    updateEdit('testimonials', t);
  };
  const removeTestimonial = (i: number) => {
    updateEdit('testimonials', (editForm.testimonials || []).filter((_: any, j: number) => j !== i));
  };

  // ── Full-page editor ──
  if (editingId) {
    return (
      <PortalLayout>
        <div className="max-w-5xl mx-auto space-y-4">
          <div className="flex items-center justify-between">
            <button onClick={() => setEditingId(null)} className="flex items-center gap-1 text-sm text-slate-400 hover:text-white">
              <ArrowLeft className="h-4 w-4" /> Back to pages
            </button>
            <div className="flex gap-2">
              <a href={`/c/${editForm.slug}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 px-3 py-1.5 rounded bg-slate-700 text-xs text-slate-300 hover:bg-slate-600">
                <Eye className="h-3 w-3" /> Preview
              </a>
              <Button onClick={saveEdit} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700">
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}
                Save Changes
              </Button>
            </div>
          </div>

          <h1 className="text-xl font-bold text-white">{editForm.title}</h1>

          {/* Tabs */}
          <div className="flex gap-1 flex-wrap border-b border-slate-700 pb-2">
            {([
              ['content', 'Content', FileText],
              ['blocks', 'Blocks', LayoutGrid],
              ['design', 'Design', Palette],
              ['form', 'Form', FormInput],
              ['seo', 'SEO', Search],
              ['countdown', 'Timer', Clock],
              ['testimonials', 'Reviews', MessageSquare],
              ['exit_intent', 'Exit Pop', MousePointerClick],
              ['languages', 'Languages', Languages],
              ['css', 'CSS', Code],
              ['ab', 'A/B', CopyPlus],
            ] as [EditTab, string, any][]).map(([key, label, Icon]) => (
              <button key={key} onClick={() => setEditTab(key)}
                className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${editTab === key ? 'bg-emerald-500/20 text-emerald-400' : 'text-slate-400 hover:text-white'}`}>
                <Icon className="h-3.5 w-3.5" /> {label}
              </button>
            ))}
          </div>

          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-5 space-y-4">

              {/* ── Content tab ── */}
              {editTab === 'content' && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Title" value={editForm.title} onChange={v => updateEdit('title', v)} />
                    <Field label="Headline" value={editForm.headline} onChange={v => updateEdit('headline', v)} />
                    <Field label="Subheadline" value={editForm.subheadline} onChange={v => updateEdit('subheadline', v)} />
                    <Field label="Hero Image URL" value={editForm.hero_image_url} onChange={v => updateEdit('hero_image_url', v)} placeholder="https://..." />
                    <div>
                      <label className="text-xs text-slate-400 block mb-1">Template</label>
                      <select value={editForm.template} onChange={e => updateEdit('template', e.target.value)} className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm">
                        {TEMPLATES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="text-xs text-slate-400 block mb-1">Vertical</label>
                      <select value={editForm.vertical} onChange={e => updateEdit('vertical', e.target.value)} className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm">
                        {VERTICALS.map(v => <option key={v} value={v}>{v.replace(/_/g, ' ')}</option>)}
                      </select>
                    </div>
                  </div>
                  <label className="flex items-center gap-2 text-sm text-slate-300">
                    <input type="checkbox" checked={editForm.is_published || false} onChange={e => updateEdit('is_published', e.target.checked)} className="rounded border-slate-600" />
                    Published (Live)
                  </label>
                </div>
              )}

              {/* ── Design tab ── */}
              {editTab === 'design' && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="CTA Button Text" value={editForm.cta_text} onChange={v => updateEdit('cta_text', v)} placeholder="Get Your Free Quote" />
                    <ColorField label="CTA Button Color" value={editForm.cta_color || '#10b981'} onChange={v => updateEdit('cta_color', v)} />
                    <ColorField label="Background Color" value={editForm.background_color || '#0f172a'} onChange={v => updateEdit('background_color', v)} />
                    <ColorField label="Text Color" value={editForm.text_color || '#ffffff'} onChange={v => updateEdit('text_color', v)} />
                  </div>
                  {/* Preview */}
                  <div className="rounded-lg overflow-hidden border border-slate-700" style={{ background: editForm.background_color || '#0f172a' }}>
                    {editForm.hero_image_url && (
                      <img src={editForm.hero_image_url} alt="" className="w-full h-32 object-cover" />
                    )}
                    <div className="p-6 text-center">
                      <h2 className="text-lg font-bold" style={{ color: editForm.text_color || '#fff' }}>{editForm.headline || 'Your Headline'}</h2>
                      {editForm.subheadline && <p className="text-sm mt-1 opacity-70" style={{ color: editForm.text_color || '#fff' }}>{editForm.subheadline}</p>}
                      <button className="mt-4 px-6 py-2 rounded-lg text-white font-semibold text-sm" style={{ background: editForm.cta_color || '#10b981' }}>
                        {editForm.cta_text || 'Get Your Free Quote'}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* ── SEO tab ── */}
              {editTab === 'seo' && (
                <div className="space-y-4">
                  <Field label="Meta Title (for Google)" value={editForm.meta_title} onChange={v => updateEdit('meta_title', v)} placeholder="Best Auto Insurance Rates | Compare & Save" />
                  <div>
                    <label className="text-xs text-slate-400 block mb-1">Meta Description</label>
                    <textarea value={editForm.meta_description || ''} onChange={e => updateEdit('meta_description', e.target.value)} placeholder="Compare auto insurance rates from top providers..." rows={3} className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm" />
                    <span className="text-[10px] text-slate-500">{(editForm.meta_description || '').length}/160 characters</span>
                  </div>
                  <div className="rounded-lg bg-white p-3 text-left">
                    <div className="text-blue-600 text-sm font-medium">{editForm.meta_title || editForm.title || 'Page Title'}</div>
                    <div className="text-green-700 text-xs">intentdrive.ai/c/{editForm.slug}</div>
                    <div className="text-gray-600 text-xs mt-0.5">{editForm.meta_description || 'No description set'}</div>
                  </div>
                </div>
              )}

              {/* ── Countdown tab ── */}
              {editTab === 'countdown' && (
                <div className="space-y-4">
                  <label className="flex items-center gap-2 text-sm text-slate-300">
                    <input type="checkbox" checked={editForm.countdown_enabled || false} onChange={e => updateEdit('countdown_enabled', e.target.checked)} className="rounded border-slate-600" />
                    Enable countdown timer
                  </label>
                  {editForm.countdown_enabled && (
                    <>
                      <Field label="Countdown Text" value={editForm.countdown_text} onChange={v => updateEdit('countdown_text', v)} placeholder="Offer expires in" />
                      <div>
                        <label className="text-xs text-slate-400 block mb-1">End Time</label>
                        <input type="datetime-local" value={(editForm.countdown_end_time || '').slice(0, 16)} onChange={e => updateEdit('countdown_end_time', e.target.value)} className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm" />
                      </div>
                      <div className="rounded-lg bg-slate-900 p-4 text-center border border-amber-500/30">
                        <div className="text-xs text-amber-400 mb-1">{editForm.countdown_text || 'Offer expires in'}</div>
                        <div className="text-2xl font-mono font-bold text-white">02 : 14 : 33</div>
                        <div className="text-[10px] text-slate-500 mt-1">hours : minutes : seconds</div>
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* ── Testimonials tab ── */}
              {editTab === 'testimonials' && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-300">{(editForm.testimonials || []).length} testimonials</span>
                    <button onClick={addTestimonial} className="flex items-center gap-1 px-3 py-1.5 rounded bg-emerald-600 text-xs text-white hover:bg-emerald-700">
                      <Plus className="h-3 w-3" /> Add
                    </button>
                  </div>
                  {(editForm.testimonials || []).map((t: Testimonial, i: number) => (
                    <Card key={i} className="bg-slate-900/50 border-slate-700">
                      <CardContent className="p-3 space-y-2">
                        <div className="grid grid-cols-2 gap-2">
                          <input value={t.name} onChange={e => updateTestimonial(i, 'name', e.target.value)} placeholder="Name" className="bg-slate-800 border border-slate-600 rounded px-2 py-1.5 text-white text-xs" />
                          <input value={t.location || ''} onChange={e => updateTestimonial(i, 'location', e.target.value)} placeholder="Location (e.g. CA)" className="bg-slate-800 border border-slate-600 rounded px-2 py-1.5 text-white text-xs" />
                        </div>
                        <textarea value={t.quote} onChange={e => updateTestimonial(i, 'quote', e.target.value)} placeholder="What they said..." rows={2} className="w-full bg-slate-800 border border-slate-600 rounded px-2 py-1.5 text-white text-xs" />
                        <div className="flex items-center justify-between">
                          <div className="flex gap-1">
                            {[1, 2, 3, 4, 5].map(star => (
                              <button key={star} onClick={() => updateTestimonial(i, 'rating', star)} className={`text-sm ${(t.rating || 5) >= star ? 'text-amber-400' : 'text-slate-600'}`}>
                                ★
                              </button>
                            ))}
                          </div>
                          <button onClick={() => removeTestimonial(i)} className="text-xs text-slate-500 hover:text-red-400">
                            <Trash2 className="h-3 w-3" />
                          </button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}

              {/* ── CSS tab ── */}
              {/* ── Blocks tab (drag-and-drop page builder) ── */}
              {editTab === 'blocks' && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-slate-400">Add and reorder content blocks to build your page layout.</p>
                    <div className="flex gap-1">
                      {([
                        ['hero', 'Hero', ImageIcon],
                        ['text', 'Text', Type],
                        ['image', 'Image', ImageIcon],
                        ['widget', 'Widget', LayoutGrid],
                        ['cta', 'CTA', MousePointerClick],
                        ['features', 'Features', ListChecks],
                        ['divider', 'Divider', GripVertical],
                      ] as [string, string, any][]).map(([type, label, Icon]) => (
                        <button key={type} onClick={() => {
                          const blocks = editForm.blocks || [];
                          updateEdit('blocks', [...blocks, {
                            id: Date.now().toString(36),
                            type,
                            content: type === 'hero' ? { headline: 'Your Headline', subheadline: 'Subheadline text', image_url: '' }
                              : type === 'text' ? { text: 'Your content here...', align: 'left' }
                              : type === 'image' ? { url: '', alt: '', width: '100%' }
                              : type === 'widget' ? { vertical: editForm.vertical || 'auto_insurance' }
                              : type === 'cta' ? { text: 'Get Started', url: '#widget', color: editForm.cta_color || '#10b981' }
                              : type === 'features' ? { items: ['Feature 1', 'Feature 2', 'Feature 3'] }
                              : {},
                          }]);
                        }} className="flex items-center gap-1 px-2 py-1 rounded bg-slate-700 text-[10px] text-slate-300 hover:bg-slate-600">
                          <Icon className="h-3 w-3" /> {label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {(editForm.blocks || []).length === 0 ? (
                    <div className="border-2 border-dashed border-slate-700 rounded-lg p-8 text-center text-sm text-slate-500">
                      Click a block type above to start building your page
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {(editForm.blocks || []).map((block: PageBlock, i: number) => (
                        <Card key={block.id} className="bg-slate-900/50 border-slate-700">
                          <CardContent className="p-3">
                            <div className="flex items-center gap-2 mb-2">
                              <GripVertical className="h-4 w-4 text-slate-600 cursor-grab" />
                              <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-emerald-500/20 text-emerald-400">{block.type}</span>
                              <div className="flex-1" />
                              <button disabled={i === 0} onClick={() => {
                                const b = [...(editForm.blocks || [])];
                                [b[i-1], b[i]] = [b[i], b[i-1]];
                                updateEdit('blocks', b);
                              }} className="text-slate-600 hover:text-white disabled:opacity-30"><ArrowUp className="h-3 w-3" /></button>
                              <button disabled={i === (editForm.blocks || []).length - 1} onClick={() => {
                                const b = [...(editForm.blocks || [])];
                                [b[i], b[i+1]] = [b[i+1], b[i]];
                                updateEdit('blocks', b);
                              }} className="text-slate-600 hover:text-white disabled:opacity-30"><ArrowDown className="h-3 w-3" /></button>
                              <button onClick={() => updateEdit('blocks', (editForm.blocks || []).filter((_: any, j: number) => j !== i))} className="text-slate-600 hover:text-red-400"><Trash2 className="h-3 w-3" /></button>
                            </div>
                            {/* Block-specific content editor */}
                            {block.type === 'hero' && (
                              <div className="grid grid-cols-2 gap-2">
                                <input value={(block.content.headline as string) || ''} onChange={e => { const b = [...(editForm.blocks || [])]; b[i] = { ...b[i], content: { ...b[i].content, headline: e.target.value } }; updateEdit('blocks', b); }} placeholder="Headline" className="bg-slate-800 border border-slate-600 rounded px-2 py-1.5 text-white text-xs" />
                                <input value={(block.content.image_url as string) || ''} onChange={e => { const b = [...(editForm.blocks || [])]; b[i] = { ...b[i], content: { ...b[i].content, image_url: e.target.value } }; updateEdit('blocks', b); }} placeholder="Image URL" className="bg-slate-800 border border-slate-600 rounded px-2 py-1.5 text-white text-xs" />
                              </div>
                            )}
                            {block.type === 'text' && (
                              <textarea value={(block.content.text as string) || ''} onChange={e => { const b = [...(editForm.blocks || [])]; b[i] = { ...b[i], content: { ...b[i].content, text: e.target.value } }; updateEdit('blocks', b); }} rows={3} className="w-full bg-slate-800 border border-slate-600 rounded px-2 py-1.5 text-white text-xs" />
                            )}
                            {block.type === 'cta' && (
                              <div className="grid grid-cols-2 gap-2">
                                <input value={(block.content.text as string) || ''} onChange={e => { const b = [...(editForm.blocks || [])]; b[i] = { ...b[i], content: { ...b[i].content, text: e.target.value } }; updateEdit('blocks', b); }} placeholder="Button text" className="bg-slate-800 border border-slate-600 rounded px-2 py-1.5 text-white text-xs" />
                                <input value={(block.content.url as string) || ''} onChange={e => { const b = [...(editForm.blocks || [])]; b[i] = { ...b[i], content: { ...b[i].content, url: e.target.value } }; updateEdit('blocks', b); }} placeholder="URL" className="bg-slate-800 border border-slate-600 rounded px-2 py-1.5 text-white text-xs" />
                              </div>
                            )}
                            {block.type === 'image' && (
                              <input value={(block.content.url as string) || ''} onChange={e => { const b = [...(editForm.blocks || [])]; b[i] = { ...b[i], content: { ...b[i].content, url: e.target.value } }; updateEdit('blocks', b); }} placeholder="Image URL" className="w-full bg-slate-800 border border-slate-600 rounded px-2 py-1.5 text-white text-xs" />
                            )}
                            {block.type === 'features' && (
                              <div className="space-y-1">
                                {((block.content.items as string[]) || []).map((item: string, fi: number) => (
                                  <div key={fi} className="flex gap-1">
                                    <input value={item} onChange={e => { const b = [...(editForm.blocks || [])]; const items = [...((b[i].content.items as string[]) || [])]; items[fi] = e.target.value; b[i] = { ...b[i], content: { ...b[i].content, items } }; updateEdit('blocks', b); }} className="flex-1 bg-slate-800 border border-slate-600 rounded px-2 py-1 text-white text-xs" />
                                    <button onClick={() => { const b = [...(editForm.blocks || [])]; const items = ((b[i].content.items as string[]) || []).filter((_: any, j: number) => j !== fi); b[i] = { ...b[i], content: { ...b[i].content, items } }; updateEdit('blocks', b); }} className="text-slate-600 hover:text-red-400"><X className="h-3 w-3" /></button>
                                  </div>
                                ))}
                                <button onClick={() => { const b = [...(editForm.blocks || [])]; const items = [...((b[i].content.items as string[]) || []), 'New feature']; b[i] = { ...b[i], content: { ...b[i].content, items } }; updateEdit('blocks', b); }} className="text-[10px] text-emerald-400 hover:underline">+ Add item</button>
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* ── Form builder tab ── */}
              {editTab === 'form' && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-slate-400">Build a custom lead capture form on the landing page.</p>
                    <button onClick={() => {
                      const fields = editForm.form_fields || [];
                      updateEdit('form_fields', [...fields, { id: Date.now().toString(36), label: 'New Field', type: 'text', placeholder: '', required: false }]);
                    }} className="flex items-center gap-1 px-3 py-1.5 rounded bg-emerald-600 text-xs text-white hover:bg-emerald-700">
                      <Plus className="h-3 w-3" /> Add Field
                    </button>
                  </div>
                  {(editForm.form_fields || []).length === 0 ? (
                    <div className="border-2 border-dashed border-slate-700 rounded-lg p-8 text-center text-sm text-slate-500">
                      No custom form fields. The widget's built-in profiler handles data collection by default.
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {(editForm.form_fields || []).map((field: FormField, i: number) => (
                        <Card key={field.id} className="bg-slate-900/50 border-slate-700">
                          <CardContent className="p-3">
                            <div className="grid grid-cols-4 gap-2">
                              <input value={field.label} onChange={e => { const f = [...(editForm.form_fields || [])]; f[i] = { ...f[i], label: e.target.value }; updateEdit('form_fields', f); }} placeholder="Label" className="bg-slate-800 border border-slate-600 rounded px-2 py-1.5 text-white text-xs" />
                              <select value={field.type} onChange={e => { const f = [...(editForm.form_fields || [])]; f[i] = { ...f[i], type: e.target.value as any }; updateEdit('form_fields', f); }} className="bg-slate-800 border border-slate-600 rounded px-2 py-1.5 text-white text-xs">
                                <option value="text">Text</option>
                                <option value="email">Email</option>
                                <option value="phone">Phone</option>
                                <option value="select">Dropdown</option>
                                <option value="checkbox">Checkbox</option>
                                <option value="textarea">Textarea</option>
                              </select>
                              <input value={field.placeholder || ''} onChange={e => { const f = [...(editForm.form_fields || [])]; f[i] = { ...f[i], placeholder: e.target.value }; updateEdit('form_fields', f); }} placeholder="Placeholder" className="bg-slate-800 border border-slate-600 rounded px-2 py-1.5 text-white text-xs" />
                              <div className="flex items-center gap-2">
                                <label className="flex items-center gap-1 text-[10px] text-slate-400">
                                  <input type="checkbox" checked={field.required} onChange={e => { const f = [...(editForm.form_fields || [])]; f[i] = { ...f[i], required: e.target.checked }; updateEdit('form_fields', f); }} className="rounded border-slate-600" />
                                  Req
                                </label>
                                <button onClick={() => updateEdit('form_fields', (editForm.form_fields || []).filter((_: any, j: number) => j !== i))} className="text-slate-600 hover:text-red-400"><Trash2 className="h-3 w-3" /></button>
                              </div>
                            </div>
                            {field.type === 'select' && (
                              <input value={(field.options || []).join(', ')} onChange={e => { const f = [...(editForm.form_fields || [])]; f[i] = { ...f[i], options: e.target.value.split(',').map(s => s.trim()) }; updateEdit('form_fields', f); }} placeholder="Option 1, Option 2, Option 3" className="mt-2 w-full bg-slate-800 border border-slate-600 rounded px-2 py-1.5 text-white text-xs" />
                            )}
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                  {/* Form preview */}
                  {(editForm.form_fields || []).length > 0 && (
                    <Card className="bg-slate-900/50 border-slate-700">
                      <CardHeader className="pb-1"><CardTitle className="text-xs text-slate-500">Preview</CardTitle></CardHeader>
                      <CardContent className="p-4 space-y-3">
                        {(editForm.form_fields || []).map((f: FormField) => (
                          <div key={f.id}>
                            <label className="text-xs text-slate-300 mb-1 block">{f.label} {f.required && <span className="text-red-400">*</span>}</label>
                            {f.type === 'textarea' ? (
                              <textarea placeholder={f.placeholder} rows={3} className="w-full bg-slate-800 border border-slate-600 rounded px-3 py-2 text-white text-sm" readOnly />
                            ) : f.type === 'select' ? (
                              <select className="w-full bg-slate-800 border border-slate-600 rounded px-3 py-2 text-white text-sm" disabled>
                                <option>{f.placeholder || 'Select...'}</option>
                                {(f.options || []).map(o => <option key={o}>{o}</option>)}
                              </select>
                            ) : f.type === 'checkbox' ? (
                              <label className="flex items-center gap-2 text-sm text-slate-300"><input type="checkbox" disabled className="rounded" /> {f.placeholder || f.label}</label>
                            ) : (
                              <input type={f.type} placeholder={f.placeholder} className="w-full bg-slate-800 border border-slate-600 rounded px-3 py-2 text-white text-sm" readOnly />
                            )}
                          </div>
                        ))}
                      </CardContent>
                    </Card>
                  )}
                </div>
              )}

              {/* ── Exit Intent tab ── */}
              {editTab === 'exit_intent' && (
                <div className="space-y-4">
                  <label className="flex items-center gap-2 text-sm text-slate-300">
                    <input type="checkbox" checked={editForm.exit_intent_enabled || false} onChange={e => updateEdit('exit_intent_enabled', e.target.checked)} className="rounded border-slate-600" />
                    Enable exit-intent popup
                  </label>
                  {editForm.exit_intent_enabled && (
                    <>
                      <Field label="Popup Headline" value={editForm.exit_intent_headline} onChange={v => updateEdit('exit_intent_headline', v)} placeholder="Wait! Don't miss this offer" />
                      <Field label="CTA Button Text" value={editForm.exit_intent_cta} onChange={v => updateEdit('exit_intent_cta', v)} placeholder="Get My Quote" />
                      <Field label="Delay (seconds before enabling)" value={editForm.exit_intent_delay_seconds} onChange={v => updateEdit('exit_intent_delay_seconds', parseInt(v) || 0)} placeholder="0" />
                      {/* Preview */}
                      <div className="rounded-lg border border-slate-700 overflow-hidden">
                        <div className="bg-black/60 p-6">
                          <div className="max-w-sm mx-auto bg-slate-800 rounded-xl p-6 text-center border border-slate-600">
                            <MousePointerClick className="h-8 w-8 text-amber-400 mx-auto mb-3" />
                            <h3 className="text-lg font-bold text-white mb-2">{editForm.exit_intent_headline || "Wait! Don't miss this offer"}</h3>
                            <p className="text-xs text-slate-400 mb-4">We have exclusive rates just for you.</p>
                            <button className="px-6 py-2 rounded-lg text-white font-semibold text-sm" style={{ background: editForm.cta_color || '#10b981' }}>
                              {editForm.exit_intent_cta || 'Get My Quote'}
                            </button>
                            <div className="mt-3 text-[10px] text-slate-500 cursor-pointer hover:text-slate-400">No thanks, I'll pass</div>
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* ── Languages tab ── */}
              {editTab === 'languages' && (
                <div className="space-y-4">
                  <div>
                    <label className="text-xs text-slate-400 block mb-1">Primary Language</label>
                    <select value={editForm.language || 'en'} onChange={e => updateEdit('language', e.target.value)} className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm">
                      <option value="en">English</option>
                      <option value="es">Spanish (Espa\u00f1ol)</option>
                      <option value="fr">French (Fran\u00e7ais)</option>
                      <option value="pt">Portuguese (Portugu\u00eas)</option>
                      <option value="de">German (Deutsch)</option>
                      <option value="zh">Chinese (\u4e2d\u6587)</option>
                    </select>
                  </div>
                  <p className="text-xs text-slate-400">Add translations for key text on your landing page. Leave blank to use the primary language value.</p>
                  <div className="space-y-3">
                    {['headline', 'subheadline', 'cta_text', 'meta_title', 'meta_description'].map(field => (
                      <div key={field} className="space-y-1">
                        <label className="text-xs text-slate-500">{field.replace(/_/g, ' ')}</label>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <span className="text-[9px] text-slate-600 block mb-0.5">EN (primary)</span>
                            <div className="bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-xs text-slate-400 truncate">
                              {(editForm as any)[field] || '—'}
                            </div>
                          </div>
                          <div>
                            <span className="text-[9px] text-slate-600 block mb-0.5">ES (Spanish)</span>
                            <input
                              value={(editForm.translations || {})?.[field]?.es || ''}
                              onChange={e => {
                                const t = { ...(editForm.translations || {}) };
                                t[field] = { ...(t[field] || {}), es: e.target.value };
                                updateEdit('translations', t);
                              }}
                              placeholder={`Spanish ${field.replace(/_/g, ' ')}...`}
                              className="w-full bg-slate-800 border border-slate-600 rounded px-2 py-1.5 text-white text-xs"
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {editTab === 'css' && (
                <div className="space-y-3">
                  <p className="text-xs text-slate-400">Custom CSS applied to this landing page only. Use to override default styles.</p>
                  <textarea value={editForm.custom_css || ''} onChange={e => updateEdit('custom_css', e.target.value)} placeholder={`/* Custom styles */\n.hero { padding: 60px 20px; }\n.cta-button { border-radius: 999px; }`} rows={12} className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-emerald-400 text-xs font-mono" />
                </div>
              )}

              {/* ── A/B tab ── */}
              {editTab === 'ab' && (
                <div className="space-y-4">
                  <p className="text-xs text-slate-400">Create a variant of this page and split traffic between them.</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-slate-400 block mb-1">Variant</label>
                      <select value={editForm.ab_variant || 'A'} onChange={e => updateEdit('ab_variant', e.target.value)} className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm">
                        <option value="A">A (Control)</option>
                        <option value="B">B (Variant)</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-xs text-slate-400 block mb-1">Traffic %</label>
                      <input type="number" min={1} max={100} value={editForm.ab_traffic_percent || 100} onChange={e => updateEdit('ab_traffic_percent', parseInt(e.target.value) || 100)} className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm" />
                    </div>
                  </div>
                  <div className="rounded-lg bg-slate-900 p-3 border border-slate-700">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs text-slate-400">Traffic Split</span>
                    </div>
                    <div className="h-4 rounded-full overflow-hidden flex">
                      <div className="bg-emerald-500" style={{ width: `${editForm.ab_traffic_percent || 100}%` }} />
                      <div className="bg-violet-500 flex-1" />
                    </div>
                    <div className="flex justify-between text-[10px] text-slate-500 mt-1">
                      <span>A: {editForm.ab_traffic_percent || 100}%</span>
                      <span>B: {100 - (editForm.ab_traffic_percent || 100)}%</span>
                    </div>
                  </div>
                </div>
              )}

            </CardContent>
          </Card>
        </div>
      </PortalLayout>
    );
  }

  // ── List view ──
  return (
    <PortalLayout>
      <div className="max-w-5xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <FileText className="h-6 w-6 text-emerald-400" />
              Landing Pages
            </h1>
            <p className="text-sm text-slate-400 mt-1">Create high-converting landing pages with the widget embedded.</p>
          </div>
          <Button onClick={() => setShowCreate(!showCreate)} className="bg-emerald-600 hover:bg-emerald-700">
            <Plus className="h-4 w-4 mr-1" /> New Page
          </Button>
        </div>

        {showCreate && (
          <Card className="bg-slate-800/50 border-emerald-500/30">
            <CardContent className="p-5 space-y-4">
              <h3 className="text-sm font-medium text-white">New Landing Page</h3>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Page Title *" value={formTitle} onChange={setFormTitle} placeholder="Best Auto Insurance Rates 2026" />
                <Field label="Headline" value={formHeadline} onChange={setFormHeadline} placeholder="Save Up to 40% on Car Insurance" />
                <div>
                  <label className="text-xs text-slate-400 block mb-1">Template</label>
                  <select value={formTemplate} onChange={e => setFormTemplate(e.target.value)} className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm">
                    {TEMPLATES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-slate-400 block mb-1">Vertical</label>
                  <select value={formVertical} onChange={e => setFormVertical(e.target.value)} className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm">
                    {VERTICALS.map(v => <option key={v} value={v}>{v.replace(/_/g, ' ')}</option>)}
                  </select>
                </div>
              </div>
              {error && <div className="px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/30 text-xs text-red-400">{error}</div>}
              <div className="flex gap-2">
                <Button onClick={create} disabled={creating || !formTitle} className="bg-emerald-600 hover:bg-emerald-700">
                  {creating ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null} Create
                </Button>
                <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
              </div>
            </CardContent>
          </Card>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 text-emerald-400 animate-spin" /></div>
        ) : pages.length === 0 ? (
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-8 text-center">
              <FileText className="h-10 w-10 text-slate-600 mx-auto mb-3" />
              <p className="text-sm text-slate-400">No landing pages yet.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {pages.map(page => (
              <Card key={page.id} className="bg-slate-800/50 border-slate-700 hover:border-slate-600 transition-colors">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h3 className="text-sm font-medium text-white">{page.title}</h3>
                      <p className="text-xs text-slate-500 mt-0.5">/{page.slug} &middot; {page.template} &middot; {page.vertical.replace(/_/g, ' ')}</p>
                    </div>
                    <div className="flex items-center gap-1">
                      {page.ab_variant !== 'A' && <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-violet-500/20 text-violet-400">{page.ab_variant}</span>}
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${page.is_published ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-600/20 text-slate-400'}`}>
                        {page.is_published ? 'Live' : 'Draft'}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-slate-400 mb-3">
                    <span>{page.views} views</span>
                    <span>{page.conversions} conv</span>
                    {page.views > 0 && <span>{(page.conversions / page.views * 100).toFixed(1)}% CVR</span>}
                    {page.countdown_enabled && <span className="text-amber-400 flex items-center gap-0.5"><Clock className="h-3 w-3" /> Timer</span>}
                    {(page.testimonials || []).length > 0 && <span className="text-blue-400">{page.testimonials.length} reviews</span>}
                  </div>
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <button onClick={() => startEdit(page)} className="flex items-center gap-1 px-2 py-1 rounded bg-emerald-600/20 text-xs text-emerald-400 hover:bg-emerald-600/30">
                      <Pencil className="h-3 w-3" /> Edit
                    </button>
                    <button onClick={() => duplicatePage(page)} className="flex items-center gap-1 px-2 py-1 rounded bg-slate-700 text-xs text-slate-300 hover:bg-slate-600">
                      <CopyPlus className="h-3 w-3" /> Duplicate
                    </button>
                    <button onClick={() => copyUrl(page.slug)} className="flex items-center gap-1 px-2 py-1 rounded bg-slate-700 text-xs text-slate-300 hover:bg-slate-600">
                      {copied === page.slug ? <Check className="h-3 w-3 text-emerald-400" /> : <Copy className="h-3 w-3" />} URL
                    </button>
                    <a href={`/c/${page.slug}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 px-2 py-1 rounded bg-slate-700 text-xs text-slate-300 hover:bg-slate-600">
                      <Eye className="h-3 w-3" />
                    </a>
                    <button onClick={() => deletePage(page.id)} className="flex items-center gap-1 px-2 py-1 rounded text-xs text-slate-600 hover:text-red-400">
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </PortalLayout>
  );
}

// ── Reusable field components ──

function Field({ label, value, onChange, placeholder }: { label: string; value: any; onChange: (v: string) => void; placeholder?: string }) {
  return (
    <div>
      <label className="text-xs text-slate-400 block mb-1">{label}</label>
      <input value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder} className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm" />
    </div>
  );
}

function ColorField({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <div>
      <label className="text-xs text-slate-400 block mb-1">{label}</label>
      <div className="flex gap-2">
        <input type="color" value={value} onChange={e => onChange(e.target.value)} className="w-10 h-10 rounded border border-slate-600 cursor-pointer" />
        <input value={value} onChange={e => onChange(e.target.value)} className="flex-1 bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm font-mono" />
      </div>
    </div>
  );
}
